import { Icon } from "@iconify/react";
import { useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { Skeleton } from "antd";
import { Button } from "@/shared/components/button";
import { EmptyState } from "@/shared/components/EmptyState";
import { NotAvailable } from "@/shared/components/NotAvailable";
import { cn } from "@/shared/lib/utils";
import type { TimelineItemData, TimelineType } from "@/features/patient/records/utils/adaptTimeline";
import {
  useObservationHistoryByName,
  useObservationsByDate,
} from "@/features/patient/records/utils/adaptTimeline";

interface HealthTimelineProps {
  items: TimelineItemData[];
  isLoading?: boolean;
}

const FHIR_STATUS_LABELS: Record<string, string> = {
  final: "Final",
  preliminary: "Under Review",
  registered: "Registered",
  amended: "Amended",
  corrected: "Corrected",
  active: "Active",
  completed: "Completed",
  finished: "Completed",
  "in-progress": "In Progress",
  stopped: "Stopped",
  "on-hold": "On Hold",
  cancelled: "Cancelled",
  "entered-in-error": "Entered in Error",
};

function statusLabel(status: string): string {
  if (!status) return "Recorded";
  return FHIR_STATUS_LABELS[status.toLowerCase()] ?? status;
}

const statusColors: Record<string, string> = {
  final: "text-[#40C463]",
  completed: "text-[#40C463]",
  finished: "text-[#40C463]",
  active: "text-[#F36F8F]",
  amended: "text-[#F36F8F]",
  preliminary: "text-[#9CA3AF]",
  cancelled: "text-[#9CA3AF]",
};

function statusColor(status: string): string {
  return statusColors[status.toLowerCase()] ?? "text-[#9CA3AF]";
}

const filters = [
  { label: "All", type: "all", className: "w-14" },
  { label: "Lab Results", type: "lab_result", className: "w-28" },
  { label: "Imaging", type: "imaging", className: "w-20" },
  { label: "Prescriptions", type: "prescription", className: "w-28" },
  { label: "Immunizations", type: "immunization", className: "w-32" },
] as const;

const itemIcons: Record<TimelineType, string> = {
  lab_result: "lucide:beaker",
  prescription: "lucide:link-2",
  visit_summary: "lucide:stethoscope",
  imaging: "lucide:scan-line",
  immunization: "lucide:syringe",
  procedure: "lucide:activity-square",
  condition: "lucide:alert-circle",
};

const typeLabels: Record<TimelineType, string> = {
  lab_result: "Lab Result",
  prescription: "Prescription",
  visit_summary: "Visit Summary",
  imaging: "Imaging",
  immunization: "Immunization",
  procedure: "Procedure",
  condition: "Condition",
};

function fieldStr(item: TimelineItemData, key: string): string {
  const v = item.record.fields[key];
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function detailCards(item: TimelineItemData): { label: string; value: string }[] {
  const f = item.record.fields;
  const s = (k: string) => fieldStr(item, k);
  switch (item.type) {
    case "prescription":
      return [
        { label: "Dose", value: [s("dose_quantity"), s("dose_unit")].filter(Boolean).join(" ") },
        { label: "Route", value: s("route") },
        { label: "Status", value: statusLabel(s("status")) },
        {
          label: "Refills",
          value: typeof f.dispense_repeats_allowed === "number" ? String(f.dispense_repeats_allowed) : "",
        },
      ];
    case "visit_summary":
      return [
        { label: "Provider", value: item.performerDisplay },
        { label: "Type", value: s("encounter_class_display") || s("encounter_type") },
        { label: "Reason", value: s("reason_display") },
        { label: "Location", value: s("location_display") || s("service_provider") },
      ];
    case "imaging":
      return [
        { label: "Study", value: item.displayName },
        { label: "Status", value: statusLabel(s("status")) },
        { label: "Read by", value: s("interpreter_display") || s("performer_display") },
        { label: "Date", value: item.date },
      ];
    case "immunization":
      return [
        { label: "Vaccine", value: s("vaccine_display") },
        { label: "Dose", value: [s("dose_quantity"), s("dose_unit")].filter(Boolean).join(" ") },
        { label: "Site", value: s("site") },
        { label: "Route", value: s("route") },
      ];
    case "procedure":
      return [
        { label: "Procedure", value: s("display_name") },
        { label: "Status", value: statusLabel(s("status")) },
        { label: "CPT", value: s("cpt_code") },
        { label: "Reason", value: s("reason_display") },
      ];
    case "condition":
      return [
        { label: "Condition", value: s("display_name") },
        { label: "Clinical status", value: s("clinical_status") },
        { label: "Onset", value: item.date },
        { label: "ICD-10", value: s("icd10_code") },
      ];
    default:
      return [];
  }
}

function detailNotes(item: TimelineItemData): string {
  return fieldStr(item, "note") || fieldStr(item, "narrative_text") || fieldStr(item, "reason_display");
}

function groupByMonth(items: TimelineItemData[]): [string, TimelineItemData[]][] {
  const map = new Map<string, TimelineItemData[]>();
  for (const item of items) {
    if (!map.has(item.month)) map.set(item.month, []);
    map.get(item.month)!.push(item);
  }
  return [...map.entries()];
}

// ── Lab detail (values grid + trend chart) ─────────────────────────────────

function LabDetail({ item }: { item: TimelineItemData }) {
  const sameDay = useObservationsByDate(item.rawDate);
  const history = useObservationHistoryByName(item.title);

  return (
    <>
      <section className="mb-5">
        <h2 className="mb-3 text-xs font-extrabold uppercase tracking-wider text-[#6B7280]">Result Values</h2>
        {sameDay.length === 0 ? (
          <EmptyState
            icon={<Icon icon="lucide:flask-conical" className="h-8 w-8 text-[#9CA3AF]" />}
            title="No result values for this date"
            description="No lab values were recorded on the same day as this record."
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-4">
            {sameDay.map((o, i) => {
              const numeric = parseFloat(o.value);
              const outOfRange =
                !Number.isNaN(numeric) &&
                ((o.rangeLow != null && numeric < o.rangeLow) || (o.rangeHigh != null && numeric > o.rangeHigh));
              return (
                <div key={i} className="rounded-2xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-xs font-extrabold uppercase text-[#0A2947]">{o.componentName}</p>
                    <p className={cn("text-[10px] font-bold", outOfRange ? "text-[#E4002B]" : "text-[#40C463]")}>
                      {outOfRange ? "Out of range" : "In range"}
                    </p>
                  </div>
                  <div className="flex items-end gap-1">
                    <p className={cn("text-2xl font-extrabold leading-none tracking-tight", outOfRange ? "text-[#F36F8F]" : "text-[#0A2947]")}>
                      {o.value || "—"}
                    </p>
                    <p className="pb-0.5 text-[10px] font-bold text-[#0A2947]">{o.unit}</p>
                  </div>
                  <p className="mt-3 text-[10px] font-bold text-[#9CA3AF]">
                    {o.range ? `Range: ${o.range}` : "Range not available"}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
        <div className="mb-4">
          <h2 className="text-sm font-extrabold text-[#111827]">{item.title} Trend</h2>
          <p className="mt-1 text-[11px] font-medium text-[#9CA3AF]">
            {history.length >= 2
              ? `${history.length} results over time`
              : "Not enough history to chart a trend"}
          </p>
        </div>
        <div className="relative h-52">
          {history.length >= 2 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={history} margin={{ top: 8, right: 18, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="timelineFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F36F8F" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#F36F8F" stopOpacity={0.08} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E5E7EB" strokeDasharray="4 4" vertical={false} />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: "#0A2947", fontSize: 11, fontWeight: 700 }} />
                <YAxis tickLine={false} axisLine={false} tick={{ fill: "#9CA3AF", fontSize: 10, fontWeight: 700 }} />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#F36F8F"
                  strokeWidth={3}
                  fill="url(#timelineFill)"
                  dot={{ r: 3, fill: "#F36F8F", stroke: "#fff", strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState
              className="h-full justify-center bg-[#F9FAFB]"
              icon={<Icon icon="lucide:line-chart" className="h-8 w-8 text-[#9CA3AF]" />}
              title="Not enough data to chart"
              description="A trend needs at least two results for the same test."
            />
          )}
        </div>
      </section>
    </>
  );
}

// ── Generic detail (cards + notes + record info + activity) ────────────────

function GenericDetail({ item }: { item: TimelineItemData }) {
  const cards = detailCards(item);
  const notes = detailNotes(item);

  return (
    <div className="grid gap-5">
      {cards.length > 0 && (
        <section className="grid gap-4 md:grid-cols-4">
          {cards.map((card) => (
            <div key={card.label} className="rounded-2xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
              <p className="mb-3 text-xs font-extrabold uppercase text-[#0A2947]">{card.label}</p>
              <p className="text-lg font-extrabold leading-tight tracking-tight text-[#0A2947]">
                {card.value || <NotAvailable />}
              </p>
            </div>
          ))}
        </section>
      )}

      <section className="grid gap-4 lg:grid-cols-[1fr_300px]">
        <div className="rounded-2xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-sm font-extrabold text-[#111827]">Details</h2>
          <div className="rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3">
            <p className="text-xs font-extrabold uppercase tracking-wide text-[#0A2947]">Notes</p>
            <p className="mt-1 text-sm font-medium leading-6 text-[#6B7280]">
              {notes || <NotAvailable label="No notes were included with this record." />}
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-sm font-extrabold text-[#111827]">Record Info</h2>
          <div className="grid gap-4">
            {[
              ["Date", item.date || "Not available"],
              ["Category", typeLabels[item.type]],
              ["Status", statusLabel(item.status)],
              ["Source", item.meta || "Patient record"],
            ].map(([label, value], index) => (
              <div key={label} className="flex items-start gap-3">
                <span className={cn("mt-1 h-2 w-2 rounded-full", index === 0 ? "bg-[#F36F8F]" : "bg-[#0A2947]")} />
                <div>
                  <p className="text-[10px] font-extrabold uppercase text-[#9CA3AF]">{label}</p>
                  <p className="mt-0.5 text-sm font-bold text-[#0A2947]">{value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-extrabold text-[#111827]">Activity Timeline</h2>
          <span className="rounded-full bg-[#E4002B]/50 px-3 py-1 text-[10px] font-extrabold !text-white">
            {statusLabel(item.status)}
          </span>
        </div>
        <div className="relative pl-6">
          <div className="absolute left-[11px] top-2 bottom-2 w-px bg-[#E5E7EB]" />
          <div className="relative pb-0">
            <span className="absolute -left-[19px] top-1 h-3 w-3 rounded-full bg-[#F36F8F]" />
            <p className="text-sm font-extrabold text-[#0A2947]">Record added</p>
            <p className="mt-0.5 text-xs font-medium text-[#9CA3AF]">{item.createdAt}</p>
          </div>
        </div>
      </section>
    </div>
  );
}

export function HealthTimeline({ items, isLoading = false }: HealthTimelineProps) {
  const [activeFilter, setActiveFilter] = useState<(typeof filters)[number]["type"]>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filteredItems = useMemo(
    () => (activeFilter === "all" ? items : items.filter((i) => i.type === activeFilter)),
    [items, activeFilter],
  );
  const grouped = useMemo(() => groupByMonth(filteredItems), [filteredItems]);

  const selectedItem =
    filteredItems.find((i) => i.id === selectedId) ?? filteredItems[0] ?? items[0] ?? null;

  useEffect(() => {
    if (selectedItem && selectedItem.id !== selectedId) setSelectedId(selectedItem.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem?.id]);

  if (isLoading) {
    return (
      <div className="min-h-full bg-[#F7F7F7] p-4 lg:p-6">
        <div className="grid gap-5 lg:grid-cols-[340px_minmax(0,1fr)]">
          <div className="rounded-3xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
            <Skeleton active paragraph={{ rows: 10 }} />
          </div>
          <div className="rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
            <Skeleton active paragraph={{ rows: 12 }} />
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-full bg-[#F7F7F7] p-4 lg:p-6">
        <EmptyState
          icon={<Icon icon="lucide:clock" className="h-12 w-12 text-[#9CA3AF]" />}
          title="Your health timeline is empty"
          description="Once your uploaded records finish processing, dated events will appear here."
        />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#F7F7F7] p-4 lg:p-6">
      <div className="grid gap-5 lg:grid-cols-[340px_minmax(0,1fr)]">
        <aside className="sticky top-5 flex h-[calc(100vh-132px)] flex-col self-start overflow-hidden rounded-3xl border border-[#E5E7EB] bg-white shadow-sm">
          <div className="border-b border-[#E5E7EB] p-4">
            <div className="flex flex-wrap gap-2">
              {filters.slice(0, 3).map((filter) => (
                <Button
                  key={filter.type}
                  variant="outline"
                  onClick={() => setActiveFilter(filter.type)}
                  className={cn(
                    "!h-7 !rounded-full !px-2 !text-[10px] !font-bold",
                    filter.className,
                    activeFilter === filter.type
                      ? "!border-[#F36F8F] !bg-[#F36F8F] !text-white hover:!border-[#F36F8F] hover:!bg-[#F36F8F] hover:!text-white"
                      : "!border-[#E5E7EB] !bg-[#F3F4F6] !text-[#9CA3AF] hover:!border-[#D1D5DB] hover:!bg-white hover:!text-[#9CA3AF]",
                  )}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {filters.slice(3).map((filter) => (
                <Button
                  key={filter.type}
                  variant="outline"
                  onClick={() => setActiveFilter(filter.type)}
                  className={cn(
                    "!h-7 !rounded-full !px-2 !text-[10px] !font-bold",
                    filter.className,
                    activeFilter === filter.type
                      ? "!border-[#F36F8F] !bg-[#F36F8F] !text-white hover:!border-[#F36F8F] hover:!bg-[#F36F8F] hover:!text-white"
                      : "!border-[#E5E7EB] !bg-[#F3F4F6] !text-[#9CA3AF] hover:!border-[#D1D5DB] hover:!bg-white hover:!text-[#9CA3AF]",
                  )}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="grid min-h-0 flex-1 content-start gap-5 overflow-y-auto p-4">
            {grouped.length === 0 && (
              <p className="px-1 py-6 text-center text-xs text-[#9CA3AF]">No records in this category.</p>
            )}
            {grouped.map(([month, monthItems]) => (
              <section key={month} className="grid gap-3">
                <h2 className="text-[11px] font-extrabold uppercase tracking-wide text-[#0A2947]">{month}</h2>
                <div className="grid gap-4">
                  {monthItems.map((entry) => {
                    const active = entry.id === selectedItem?.id;
                    return (
                      <Button
                        key={entry.id}
                        variant="outline"
                        onClick={() => setSelectedId(entry.id)}
                        className={cn(
                          "!flex !h-auto !min-h-[70px] !w-full !items-center !gap-3 !rounded-2xl !border-[#EDEEF1] !bg-white !px-3 !py-3 !text-left !shadow-sm",
                          active
                            ? "!bg-[#FFF8F8] hover:!border-[#EDEEF1] hover:!bg-[#FFF8F8]"
                            : "hover:!border-[#F36F8F]/50 hover:!bg-white hover:!shadow-md",
                        )}
                      >
                        <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-xl", active ? "bg-[#F36F8F]" : "bg-white")}>
                          <Icon icon={itemIcons[entry.type]} className={cn("h-4 w-4", active ? "text-white" : "text-[#0A2947]")} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="truncate text-xs font-extrabold leading-tight text-[#111827]">{entry.title}</p>
                            <span className={cn("shrink-0 text-[10px] font-bold", statusColor(entry.status))}>
                              {statusLabel(entry.status)}
                            </span>
                          </div>
                          <p className="mt-0.5 text-[10px] font-medium leading-tight text-[#9CA3AF]">
                            {entry.date}
                            {entry.meta ? ` · ${entry.meta}` : ""}
                          </p>
                        </div>
                      </Button>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </aside>

        <main className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm lg:p-6">
          {selectedItem && (
            <>
              <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-[11px] font-extrabold uppercase tracking-wider text-[#F36F8F]">
                      {typeLabels[selectedItem.type]}
                    </span>
                    <span className="rounded-full bg-[#FFE8B8] px-2 py-0.5 text-[10px] font-extrabold text-[#9A5A00]">
                      {statusLabel(selectedItem.status)}
                    </span>
                  </div>
                  <h1 className="text-2xl font-extrabold leading-tight tracking-tight text-[#111827]">
                    {selectedItem.title}
                  </h1>
                  <p className="mt-1 max-w-xl text-xs font-medium leading-relaxed text-[#9CA3AF]">
                    {selectedItem.date || "Date not available"}
                    {selectedItem.performerDisplay ? ` · Ordered by ${selectedItem.performerDisplay}` : ""}
                    {selectedItem.meta ? ` · ${selectedItem.meta}` : ""}
                  </p>
                </div>
              </div>

              {selectedItem.type === "lab_result" ? (
                <LabDetail item={selectedItem} />
              ) : (
                <GenericDetail item={selectedItem} />
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
